import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { nativeOffline, offline } from "@/lib/offlineNative";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { applyTheme } from "@/lib/theme";
import { setLang, t } from "@/lib/i18n";
import { range, today, setCurrency } from "@/lib/money";

const Ctx = createContext(null);
export const useData = () => useContext(Ctx);
const KINDS = ["expenses", "incomes", "savings", "loans", "recurring"];

export function DataProvider({ children }) {
  const [db, setDb] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [mode, setMode] = useState("month");
  const [anchor, setAnchor] = useState(new Date());
  const [due, setDue] = useState([]);
  const [saving, setSaving] = useState(false);

  const busy = useRef(false);
  const pendingCreates = useRef(new Map());
  const merge = (a, b) => [...new Map([...a, ...b].map((x) => [x.id, x])).values()];

  const load = useCallback(async () => {
    if (busy.current) return;
    busy.current = true; setLoadError("");
    try {
      const { data } = await api.get("/bootstrap");
      setDb(data);
      applyTheme(data.settings.prefs.ui);
      await runRecurring();
    } catch (e) { setLoadError(errMsg(e)); throw e; } finally { busy.current = false; }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runRecurring = async () => {
    if (!navigator.onLine || (nativeOffline && offline.state.queue.length)) return;
    try {
      const r = await api.post("/recurring/run", { today: today() });
      setDb((d) => d ? ({ ...d,
        expenses: merge(d.expenses, r.data.added.filter((x) => !x.src && x.ledger_kind !== "savings")),
        incomes: merge(d.incomes, r.data.added.filter((x) => !!x.src && x.ledger_kind !== "savings")),
        savings: merge(d.savings, r.data.added.filter((x) => x.ledger_kind === "savings")),
        recurring: r.data.recurring,
      }) : d);
      if (r.data.added.length) toast.success(`${r.data.added.length} recurring transaction(s) recorded`);
      // The server response is authoritative. Merging retained reminders that
      // another signed-in device had already confirmed, skipped or snoozed.
      setDue(r.data.due);
    } catch (e) { /* recurring check is best-effort */ }
  };

  useEffect(() => {
    const refresh = () => {
      const data = offline.view()['/bootstrap'];
      if (data) { setDb(data); applyTheme(data.settings.prefs.ui); }
    };
    if (nativeOffline) window.addEventListener('bp-sync-state', refresh);
    return () => window.removeEventListener('bp-sync-state', refresh);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  // Keep open browser sessions current with changes made on other devices.
  // Android uses its offline cache/queue and refreshes on foreground instead.
  useEffect(() => {
    if (nativeOffline) return;
    const refresh = () => {
      if (document.visibilityState === "visible" && navigator.onLine) {
        load().catch(() => {});
      }
    };
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [load]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const f = () => db && applyTheme(db.settings.prefs.ui);
    mq.addEventListener("change", f);
    return () => mq.removeEventListener("change", f);
  }, [db]);

  const run = async (fn) => {
    setSaving(true);
    try { return await fn(); } catch (e) { toast.error(errMsg(e)); throw e; } finally { setSaving(false); }
  };

  const create = (kind, body) => {
    const key = `${kind}:${JSON.stringify(body)}`;
    if (pendingCreates.current.has(key)) return pendingCreates.current.get(key);
    const pending = run(async () => {
    const { data } = await api.post(`/data/${kind}`, body);
    setDb((d) => ({ ...d, [kind]: [...d[kind].filter((x) => x.id !== data.id), data] }));
    if (kind === "recurring") runRecurring();
    return data;
    }).finally(() => pendingCreates.current.delete(key));
    pendingCreates.current.set(key, pending); return pending;
  };
  const update = (kind, id, body) => run(async () => {
    const { data } = await api.put(`/data/${kind}/${id}`, body);
    setDb((d) => ({ ...d, [kind]: d[kind].map((x) => (x.id === id ? data : x)) }));
    return data;
  });
  const remove = (kind, id) => run(async () => {
    await api.delete(`/data/${kind}/${id}`);
    setDb((d) => ({ ...d, [kind]: d[kind].filter((x) => x.id !== id) }));
  });
  const saveSettings = (patch) => run(async () => {
    const { data } = await api.put("/settings", patch);
    setDb((d) => ({ ...d, settings: data }));
    if (patch.prefs) applyTheme(data.prefs.ui);
    return data;
  });
  const patchLocal = (kind, fn) => setDb((d) => ({ ...d, [kind]: fn(d[kind]) }));

  const catInfo = useCallback((id) => {
    const s = db?.settings;
    const c = s?.categories.find((x) => x.id === id) || s?.gone?.[id];
    return c ? { ...c, n: t(c.n) } : { id, e: "🧾", n: id, c: "#6b7280", u: false };
  }, [db]);

  const period = useMemo(() => range(mode, anchor), [mode, anchor]);
  setCurrency(db?.settings?.prefs?.currency);
  if (db) setLang(db.settings.prefs.lang);

  const value = { db, load, loadError, mode, setMode, anchor, setAnchor, period, create, update, remove, saveSettings, patchLocal, catInfo, due, setDue, saving, KINDS };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
